import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { WorkshopImage } from "../../../types/content";
import { resolvePublishedImages } from "../../../utils/workshopImages";
import type { ResolvedWorkshopImage } from "../../../utils/workshopImages";

/**
 * ============================================================================
 * The workshop photo gallery — thumbnails into a lightbox (D-081).
 * ============================================================================
 *
 * ----------------------------------------------------------------------------
 * IT RENDERS ON THREE OF THE FOUR DETAIL ROUTES
 * ----------------------------------------------------------------------------
 * CORRECTED 2026-08-30 (D-095). This comment said "it renders nothing today,
 * and that is the finished state — every workshop ships `images: []`", which
 * was true the day it was written and false from D-084 onward: eleven SFT
 * frames were published then and five FIEM frames in D-087. Sixteen
 * photographs render through this component now. A comment asserting an empty
 * state over a populated one is the same defect class as a stale count in
 * prose (D-066), and it is worse here, because the emptiness was the stated
 * reason for several downstream decisions.
 *
 * The GCELT session does still carry `images: []` and this component returns
 * null there — and THAT is a finished state, not a gap. D-069 established the
 * shape when the broadcast video slot shipped wired and empty, and the rule
 * that came with it holds: **there is no "photos coming soon".** A visitor is
 * not the audience for an internal redaction queue.
 *
 * ----------------------------------------------------------------------------
 * A PHOTOGRAPH CANNOT BE SHOWN BY EDITING THIS FILE
 * ----------------------------------------------------------------------------
 * Two keys, both elsewhere: `status` in `workshops.json` says whether an image
 * MAY be shown, and `WORKSHOP_GALLERY_IMAGES` says whether one EXISTS. This
 * component renders a thumbnail only when both answer yes. The withheld
 * photographs are exported from no module, so there is no file here to reach —
 * see `src/utils/workshopImages.ts`, which throws at module load if the two
 * answers disagree, and therefore fails `npm run build`.
 *
 * That matters more than it usually would. The five supplied photographs show
 * other people and other people's documents, and are withheld by an explicit
 * owner ruling (D-079) rather than by an oversight anybody should feel free to
 * correct.
 *
 * ----------------------------------------------------------------------------
 * WHY THIS DIALOG IS NOT `CertificateShowcase`'s
 * ----------------------------------------------------------------------------
 * The two are close enough that sharing one component is the obvious move, and
 * it was deliberately not made. `CertificateShowcase` is the render path for
 * the one image on this site with a redaction behind it, verified out of the
 * raster in D-079 and re-verified this session; refactoring it to serve a
 * gallery that currently holds zero pictures would put the verified path at
 * risk to remove a duplication that costs nothing today. Recorded in TODO as
 * the extraction to do once photographs actually exist — at which point both
 * call sites will be exercised and the refactor will be checkable.
 *
 * What IS shared is the behaviour, because a keyboard user does not care which
 * file it lives in: Escape closes, focus moves into the dialog on open,
 * **focus returns to the thumbnail that opened it** (without which the next Tab
 * restarts at the top of the document with nothing on screen having changed —
 * the D-068 finding about the mobile menu), and Tab is **trapped** inside the
 * dialog rather than wandering into a page hidden behind a backdrop.
 */

interface WorkshopGalleryProps {
	images: WorkshopImage[];
	/** Names the region for a screen reader — "Photographs from {title}". */
	workshopTitle: string;
}

/** An image with its binary resolved. Both keys, or it is not in this list. */
interface ResolvedImage {
	image: WorkshopImage;
	binary: string;
}

export default function WorkshopGallery({ images, workshopTitle }: WorkshopGalleryProps): JSX.Element | null {
	/* Index into `resolved`, or null for closed. An index rather than a boolean
	   because the lightbox has to know WHICH photograph it is showing. */
	const [openIndex, setOpenIndex] = useState<number | null>(null);
	const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const dialogRef = useRef<HTMLDivElement>(null);

	const close = useCallback((): void => {
		setOpenIndex((current: number | null) => {
			/* Focus goes back to the thumbnail that opened this. Read inside the
			   updater so the index is the one being closed, not a stale capture. */
			if (current !== null) triggerRefs.current[current]?.focus();
			return null;
		});
	}, []);

	useEffect(() => {
		if (openIndex === null) return undefined;

		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === "Escape") {
				close();
				return;
			}
			if (event.key !== "Tab") return;

			/* Queried live rather than captured on open, so a re-render inside
			   the dialog cannot leave the trap pointing at a detached node. */
			const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
				"a[href], button:not([disabled]), [tabindex]:not([tabindex='-1'])"
			);
			if (!focusable || focusable.length === 0) return;
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", onKeyDown);
		dialogRef.current?.focus();
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [openIndex, close]);

	/* BOTH KEYS, PER IMAGE — and the rule now lives in `workshopImages.ts`
	   because the /workshops index card is a second caller (D-095). A withheld
	   photograph and a published one with no binary both drop out there, so the
	   grid below cannot render a broken thumbnail or a picture nobody cleared. */
	const resolved: ResolvedWorkshopImage[] = resolvePublishedImages(images);

	/* Nothing to show is not an empty frame — it is no section at all. */
	if (resolved.length === 0) return null;

	const active = openIndex === null ? null : resolved[openIndex];

	return (
		<section className="mt-12" aria-labelledby="workshop-gallery-heading">
			<h2 id="workshop-gallery-heading" className="text-lg font-bold text-[var(--text-strong)]">
				Photographs
			</h2>

			<ul className="mt-5 grid list-none grid-cols-1 gap-4 p-0 sm:grid-cols-2 lg:grid-cols-3">
				{resolved.map((entry: ResolvedImage, index: number) => (
					<li key={entry.image.id}>
						<button
							ref={(node: HTMLButtonElement | null) => {
								triggerRefs.current[index] = node;
							}}
							type="button"
							onClick={() => setOpenIndex(index)}
							aria-label={`Enlarge photograph: ${entry.image.alt}`}
							className="group block w-full overflow-hidden rounded-[12px] border border-[var(--border)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
						>
							<img
								src={entry.binary}
								alt={entry.image.alt}
								loading="lazy"
								decoding="async"
								className="block h-48 w-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
							/>
						</button>
					</li>
				))}
			</ul>

			{/* A PORTAL, AND WITHOUT IT THIS MODAL IS NOT A MODAL. `.card-face`
			    carries `backdrop-filter: blur(12px)`, and a backdrop-filter makes
			    an element a CONTAINING BLOCK for its `position: fixed`
			    descendants — so `fixed inset-0` would resolve against the card
			    rather than the viewport, dimming a strip of one card while the
			    rest of the page stayed bright and clickable. That was measured on
			    a real build in D-076 ([18, -152, 350x1205] against a 390x900
			    viewport) and it is why this goes into `document.body`.
			    Only ever evaluated when a photograph is open, and nothing is open
			    on the server or on the first client render, so `document` is never
			    touched during pre-render (D-042). */}
			{active &&
				createPortal(
					<div
						className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
						role="presentation"
						onClick={close}
					>
						<div
							ref={dialogRef}
							role="dialog"
							aria-modal="true"
							aria-label={`Photograph from ${workshopTitle}`}
							tabIndex={-1}
							className="max-h-full w-full max-w-3xl rounded-[14px] bg-[var(--surface-modal)] p-4 focus:outline-none"
							onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
						>
							<div className="flex items-start justify-between gap-4">
								<p className="text-sm font-semibold text-[var(--text-strong)]">{workshopTitle}</p>
								{/* 44px minimum, which on a phone is the difference
								    between closing the dialog and reopening it (D-080). */}
								<button
									type="button"
									onClick={close}
									aria-label="Close the photograph"
									className="inline-flex min-h-[44px] shrink-0 items-center rounded-[8px] px-3 py-2 text-sm text-[var(--text-primary)] hover:text-[var(--accent-link-hover)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
								>
									Close
								</button>
							</div>
							{/* BOUNDED BY HEIGHT AS WELL AS WIDTH (D-080). `w-full`
							    alone sizes to the dialog and lets the height fall where
							    it likes: the certificate lightbox ran 171px below the
							    fold at 768 and 216px at 1280, and because the dialog
							    scrolled, nothing looked broken — the bottom of the
							    image simply was not there. 9rem is the chrome this
							    image sits inside: the dialog padding, the title row,
							    this element's top margin and the backdrop's padding. */}
							<img
								src={active.binary}
								alt={active.image.alt}
								className="mx-auto mt-4 block max-h-[calc(100vh-9rem)] w-auto max-w-full rounded-[10px] object-contain"
							/>
						</div>
					</div>,
					document.body
				)}
		</section>
	);
}
